<script setup lang="ts">
  import type { ProductComponentResource } from '@storefront/resources';
  import { useShared } from '@othercommerce/foundation';
  import { computed } from 'vue';
  import { useUnitFormatter } from '@othercommerce/domain';
  import { Features } from '@shared/features';

  interface Props {
    component: ProductComponentResource,
  }

  const props = defineProps<Props>();

  const shared = useShared();
  const unitFormatter = useUnitFormatter();

  const price = computed(() => {
    if (shared.value.includeVat) {
      return props.component.priceGross;
    } else {
      return props.component.priceNet;
    }
  });

  const quantity = computed(() => {
    return unitFormatter.short(props.component.stockUnit, props.component.amount);
  });

  const hidePrice = computed(() => {
    return Features.medicines.isEnabled && Features.medicalContractorsRegistry.isDisabled && props.component.isMedicinalProduct && !price.value;
  });
</script>
<template>
  <div class="component">
    <div class="component-photo">
      <MediaPicture :photo="component.photo" sizes="80x80 2x, 40x40" />
    </div>
    <div class="component-wrapper">
      <div class="component-name">
        {{ component.variantTitle }}
      </div>
      <div class="component-details">
        <div class="component-sku">
          {{ component.variantSku }}
        </div>
        <div class="component-price">
          <div class="component-quantity" v-html="quantity" />
          <div class="component-multiplier">
            &times;
          </div>
          <div class="component-price-value" v-if="hidePrice">
            {{ $t('SHOP.PRODUCT.ACTION.ASK_FOR_PRODUCT') }}
          </div>
          <div class="component-price-value" v-else>
            <template v-if="price > 0">
              <SfMoney :value="price" />
            </template>
            <template v-else>
              <data class="component-price-free" :value="price" v-text="$t('SHOP.PRODUCT.LABEL.EXTRA')" />
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="scss">
  :root {
    --sf-product-component-padding: 0.25rem;
    --sf-product-component-gap: 0.25rem;
    --sf-product-component-gap-price: 0.25rem;
    --sf-product-component-gap-details: 1rem;

    --sf-product-component-border-color: var(--secondary-border);

    --sf-product-component-photo-size: 40px;

    --sf-product-component-name-font-size: 0.75rem;
    --sf-product-component-name-font-weight: var(--font-weight-semi);
    --sf-product-component-name-line-height: 1.25rem;

    --sf-product-component-details-font-size: 0.75rem;
    --sf-product-component-details-line-height: 1.25rem;

    --sf-product-component-sku-font-size: 0.625rem;
    --sf-product-component-sku-color: var(--secondary-color);

    --sf-product-component-price-font-weight: var(--font-weight-bold);
    --sf-product-component-price-color-free: var(--primary-color);
    --sf-product-component-price-unit-font-size: 0.625rem;
    --sf-product-component-price-unit-color: var(--secondary-color);
  }
</style>
<style scoped lang="scss">
  .component {
    display: flex;
    align-items: flex-start;
    border-bottom: 1px solid var(--sf-product-component-border-color);
    min-height: 0;
    min-width: 0;
    width: 100%;
    padding: var(--sf-product-component-padding);
    gap: var(--sf-product-component-gap);

    &:first-child {
      border-top: 1px solid var(--sf-product-component-border-color);
    }

    .component-photo {
      flex: 0 0 var(--sf-product-component-photo-size);
      width: var(--sf-product-component-photo-size);
      height: var(--sf-product-component-photo-size);

      :deep(picture),
      :deep(img) {
        display: flex;
        width: 100%;
        height: 100%;
        object-fit: contain;
      }
    }

    .component-wrapper {
      min-width: 0;
      width: 100%;
    }

    .component-name {
      font-weight: var(--sf-product-component-name-font-weight);
      font-size: var(--sf-product-component-name-font-size);
      line-height: var(--sf-product-component-name-line-height);
    }

    .component-details {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: var(--sf-product-component-gap-details);
      font-size: var(--sf-product-component-details-font-size);
      line-height: var(--sf-product-component-details-line-height);

      .component-sku {
        font-size: var(--sf-product-component-sku-font-size);
        color: var(--sf-product-component-sku-color);
      }

      .component-price {
        display: flex;
        gap: var(--sf-product-component-gap-price);

        .component-price-value {
          font-weight: var(--sf-product-component-price-font-weight);

          .component-price-free {
            color: var(--sf-product-component-price-color-free);
          }
        }

        .component-price-unit {
          font-size: var(--sf-product-component-price-unit-font-size);
          color: var(--sf-product-component-price-unit-color);
        }
      }
    }
  }
</style>
